import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Send,
  Loader2,
  Inbox,
  ArrowLeft,
  Building2,
  User,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Messages page — used by both customers and businesses.
 *
 * Behavior:
 *   • Left rail: list of threads (dm + business_promo), most recent first.
 *   • Right pane: message stream for the selected thread with input box.
 *   • Polls every 5s while a thread is open (simple; upgrade to WS later).
 *   • Query param `?thread=<id>` deep-links a thread.
 */
function Avatar({ url, name, size = 36 }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name || "avatar"}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full bg-[#B31E1E]/10 text-[#B31E1E] font-medium flex items-center justify-center"
      style={{ width: size, height: size, fontSize: size / 2.8 }}
    >
      {initials}
    </div>
  );
}

function ThreadRow({ t, active, onClick }) {
  const other = t.other || {};
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 px-3 py-3 border-b border-border transition-colors ${
        active ? "bg-[#B31E1E]/5" : "hover:bg-gray-50"
      }`}
      data-testid={`thread-row-${t.id}`}
    >
      <Avatar
        url={other.avatar_data_url || other.logo_data_url}
        name={other.name}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {other.role === "business" ? (
            <Building2 size={11} className="text-gray-400 shrink-0" />
          ) : (
            <User size={11} className="text-gray-400 shrink-0" />
          )}
          <span className="text-sm font-medium truncate">
            {other.name || "Unknown"}
          </span>
          {t.kind === "business_promo" && (
            <Badge
              variant="outline"
              className="rounded-sm ml-1 text-[9px] font-mono h-4 px-1 border-amber-400 text-amber-700"
            >
              PROMO
            </Badge>
          )}
        </div>
        <div className="text-xs text-gray-500 truncate mt-0.5">
          {t.last_snippet || "No messages yet"}
        </div>
      </div>
      {t.unread_count > 0 && (
        <span
          className="shrink-0 min-w-[20px] h-5 rounded-full bg-[#B31E1E] text-white text-[10px] font-mono flex items-center justify-center px-1.5"
          data-testid={`thread-unread-${t.id}`}
        >
          {t.unread_count}
        </span>
      )}
    </button>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialThread = params.get("thread") || "";

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(initialThread);
  const [messages, setMessages] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const activeThread = threads.find((t) => t.id === activeId);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/threads");
      setThreads(data?.threads || []);
      if (!activeId && (data?.threads || []).length && initialThread) {
        const t = (data.threads || []).find((x) => x.id === initialThread);
        if (t) setActiveId(t.id);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingThreads(false);
    }
  }, [activeId, initialThread]);

  useEffect(() => {
    loadThreads();
    const h = setInterval(loadThreads, 15000);
    return () => clearInterval(h);
  }, [loadThreads]);

  const loadMessages = useCallback(async () => {
    if (!activeId) return;
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/threads/${activeId}/messages`);
      setMessages(data?.messages || []);
      // update unread on this thread locally
      setThreads((prev) =>
        prev.map((t) => (t.id === activeId ? { ...t, unread_count: 0 } : t))
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingMessages(false);
    }
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;
    loadMessages();
    const h = setInterval(loadMessages, 5000);
    return () => clearInterval(h);
  }, [activeId, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const openThread = (id) => {
    setActiveId(id);
    setParams({ thread: id }, { replace: true });
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/threads/${activeId}/messages`, {
        body: text,
      });
      setMessages((prev) => [...prev, data.message]);
      setDraft("");
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
      <div className="overline text-gray-500">Inbox</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2 flex items-center gap-3">
        <MessageSquare size={32} /> Messages
      </h1>
      <p className="text-gray-600 mt-2 max-w-2xl">
        Chat with friends and receive promotions from businesses you follow.
        Only businesses with active subscriptions can start a promo thread —
        you can reply, mute, or unfriend at any time.
      </p>

      <Card className="mt-6 rounded-md border border-border overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[540px]">
          {/* Threads rail */}
          <div
            className={`border-r border-border ${
              activeId ? "hidden md:block" : "block"
            }`}
            data-testid="threads-rail"
          >
            {loadingThreads && (
              <div className="p-6 text-sm text-gray-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            )}
            {!loadingThreads && threads.length === 0 && (
              <div className="p-8 text-center" data-testid="threads-empty">
                <Inbox className="mx-auto text-gray-300" size={36} />
                <p className="text-sm text-gray-500 mt-3">
                  No conversations yet.
                </p>
                {user?.role === "customer" && (
                  <p className="text-xs text-gray-400 mt-2 max-w-[250px] mx-auto">
                    Add a friend on your profile, then start a chat.
                  </p>
                )}
              </div>
            )}
            {threads.map((t) => (
              <ThreadRow
                key={t.id}
                t={t}
                active={t.id === activeId}
                onClick={() => openThread(t.id)}
              />
            ))}
          </div>

          {/* Message pane */}
          <div className={`${activeId ? "block" : "hidden md:block"} flex flex-col`}>
            {!activeId && (
              <div className="hidden md:flex flex-col items-center justify-center h-full text-gray-400 p-10">
                <MessageSquare size={40} />
                <p className="mt-3 text-sm">
                  Select a conversation to view messages.
                </p>
              </div>
            )}

            {activeId && (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-white sticky top-0 z-10">
                  <button
                    className="md:hidden -ml-1 p-1"
                    onClick={() => {
                      setActiveId("");
                      setParams({}, { replace: true });
                    }}
                    data-testid="thread-back-button"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  {activeThread?.other && (
                    <>
                      <Avatar
                        url={
                          activeThread.other.avatar_data_url ||
                          activeThread.other.logo_data_url
                        }
                        name={activeThread.other.name}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate flex items-center gap-2">
                          {activeThread.other.name}
                          {activeThread.kind === "business_promo" && (
                            <Badge
                              variant="outline"
                              className="rounded-sm text-[9px] font-mono h-4 px-1 border-amber-400 text-amber-700"
                            >
                              PROMO
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {activeThread.other.role === "business"
                            ? [
                                activeThread.other.city,
                                activeThread.other.state,
                              ]
                                .filter(Boolean)
                                .join(", ")
                            : "Friend"}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Messages */}
                <div
                  className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF5F2]"
                  data-testid="messages-scroll"
                >
                  {loadingMessages && messages.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 size={14} className="animate-spin" /> Loading messages…
                    </div>
                  )}
                  {messages.length === 0 && !loadingMessages && (
                    <p className="text-center text-sm text-gray-400 mt-8">
                      No messages yet. Say hi.
                    </p>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div
                        key={m.id}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        data-testid={`message-row-${m.id}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                            mine
                              ? "bg-[#B31E1E] text-white rounded-br-sm"
                              : m.kind === "promo"
                              ? "bg-amber-50 text-amber-950 border border-amber-200 rounded-bl-sm"
                              : "bg-white border border-border rounded-bl-sm"
                          }`}
                        >
                          {m.kind === "promo" && !mine && (
                            <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700 mb-1">
                              Promotion
                            </div>
                          )}
                          {m.body}
                          <div
                            className={`mt-1 text-[10px] font-mono ${
                              mine ? "text-white/70" : "text-gray-400"
                            }`}
                          >
                            {new Date(m.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {/* Composer */}
                <div className="border-t border-border p-3 bg-white flex items-end gap-2">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={
                      activeThread?.kind === "business_promo" &&
                      user?.role === "business"
                        ? "Send a promo update to this customer (24h cooldown applies)…"
                        : "Write a message…"
                    }
                    rows={2}
                    className="rounded-sm resize-none"
                    data-testid="message-input"
                    disabled={sending}
                  />
                  <Button
                    onClick={send}
                    disabled={sending || !draft.trim()}
                    className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]"
                    data-testid="message-send-button"
                  >
                    {sending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <>
                        <Send size={14} className="mr-1" /> Send
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
